
/**
 * Predefined responses for the demo chat interface
 */

export type DemoResponse = {
  content: string;
  isSignupPrompt?: boolean;
};

// Maximum number of messages before showing signup prompt
export const MAX_FREE_MESSAGES = 3; // Reduced from 5 to show signup prompt faster

/**
 * Get a predefined response based on the user's message
 */
export function getDemoResponse(message: string): DemoResponse {
  const lowerMessage = message.toLowerCase().trim();
  
  // Check for marketing-related keywords
  if (lowerMessage.includes("social media") || lowerMessage.includes("post") || lowerMessage.includes("content")) {
    return {
      content: "✨ Here's an example social media post I created for you:\n\n🚀 Ready to scale your business? Let our AI staff team handle your routine tasks while you focus on strategy. Try VirtuStaff today!\n\nWant to customize your AI staff for your specific brand voice? 💫"
    };
  }
  
  if (lowerMessage.includes("what") && (lowerMessage.includes("do") || lowerMessage.includes("virtustaff"))) {
    return {
      content: "🤖 Your VirtuStaff team can automate a variety of business tasks across marketing, sales, and support functions. Each AI staff member specializes in different areas. Try asking about specific tasks like 'create a social media post' or 'help with email marketing'! 💡"
    };
  }
  
  if (lowerMessage.includes("email") || lowerMessage.includes("newsletter")) {
    return {
      content: "📧 Here's a sample email marketing template I created:\n\nSubject: Transform Your Business with AI Staff ✨\n\nHi [Name],\n\nDiscover how VirtuStaff.ai can automate your business workflows and save your team 15+ hours per week. 🚀\n\nInterested in customizing your AI staff for your specific business needs? Let's chat! 💬"
    };
  }
  
  if (lowerMessage.includes("how") && lowerMessage.includes("work")) {
    return {
      content: "🔍 VirtuStaff works by adding specialized AI staff members to your team. Each AI specializes in a business function like Marketing 📣, Sales 💰, or Support 🛟. You train them on your business processes and they integrate with your existing tools. Different staff members have their own skills - try asking about specific capabilities of each role! ⭐"
    };
  }
  
  if (lowerMessage.includes("price") || lowerMessage.includes("cost") || lowerMessage.includes("plan")) {
    return {
      content: "💰 VirtuStaff offers flexible pricing plans starting from $29/month per AI staff member. Each plan includes different features and integration options:\n\n• Basic: Essential features for small teams 🔹\n• Pro: Advanced capabilities for growing businesses 🔷\n• Enterprise: Custom solutions for larger organizations 💎\n\nYou can hire multiple staff members for different business functions as your needs grow! 📈"
    };
  }
  
  if (lowerMessage.includes("slack") || lowerMessage.includes("integrate") || lowerMessage.includes("integration")) {
    return {
      content: "🔗 VirtuStaff integrates seamlessly with Slack! Your AI assistants can work directly in your team's Slack channels, responding to requests, generating content, and providing insights without leaving your workspace. Setup takes just a few minutes with our guided integration wizard. Would you like to learn more about our other integrations? 🤝"
    };
  }
  
  // Default response for messages that don't match any patterns
  return {
    content: "✨ I'm part of your VirtuStaff AI team! We can help with marketing, sales, support, and more. Ask me to create content, analyze data, or automate tasks for your business! 🚀",
    isSignupPrompt: Math.random() > 0.5 // Increased chance of signup prompt
  };
}

/**
 * Get the final signup prompt message
 */
export function getSignupPromptMessage(): DemoResponse {
  return {
    content: "🎉 You've reached the end of this demo. Ready to build your own AI staff team? Our AI assistants can help with everything from content creation to customer support and data analysis!",
    isSignupPrompt: true
  };
}

/**
 * Suggestions for the demo interface
 */
export const demoSuggestions = [
  "What can VirtuStaff do? 🤖",
  "Create a social media post ✍️",
  "Help with email marketing 📧",
  "How does Slack integration work? 🔗",
  "Tell me about your pricing 💰"
];
